export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type ViewStateEnvelope<T> = {
  version: number;
  updatedAt: string;
  userId: string;
  routeKey: string;
  contextKey: string;
  state: T;
};

export const DEFAULT_VIEW_STATE_VERSION = 1;
const keyPrefix = 'courtcaptain:view-state';

function safeKeyPart(value: string | null | undefined): string {
  return encodeURIComponent((value || 'default').trim() || 'default');
}

export function viewStateStorageKey(
  userId: string,
  routeKey: string,
  contextKey = 'default',
  version = DEFAULT_VIEW_STATE_VERSION,
): string {
  return `${keyPrefix}:v${version}:${safeKeyPart(userId)}:${safeKeyPart(routeKey)}:${safeKeyPart(contextKey)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function readViewStateEnvelope<T>(
  storage: StorageLike,
  userId: string | null | undefined,
  routeKey: string,
  contextKey = 'default',
  version = DEFAULT_VIEW_STATE_VERSION,
  validate?: (state: unknown) => state is T,
): ViewStateEnvelope<T> | null {
  if (!userId) return null;
  const key = viewStateStorageKey(userId, routeKey, contextKey, version);
  const raw = storage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isRecord(parsed) || parsed.version !== version || parsed.userId !== userId || parsed.routeKey !== routeKey || parsed.contextKey !== contextKey || !('state' in parsed)) {
      storage.removeItem(key);
      return null;
    }
    if (validate && !validate(parsed.state)) {
      storage.removeItem(key);
      return null;
    }
    return parsed as ViewStateEnvelope<T>;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

export function saveViewStateEnvelope<T>(
  storage: StorageLike,
  envelope: Omit<ViewStateEnvelope<T>, 'version' | 'updatedAt'> & { version?: number; updatedAt?: string },
): void {
  const version = envelope.version ?? DEFAULT_VIEW_STATE_VERSION;
  const fullEnvelope: ViewStateEnvelope<T> = {
    version,
    updatedAt: envelope.updatedAt || new Date().toISOString(),
    userId: envelope.userId,
    routeKey: envelope.routeKey,
    contextKey: envelope.contextKey,
    state: envelope.state,
  };
  storage.setItem(
    viewStateStorageKey(envelope.userId, envelope.routeKey, envelope.contextKey, version),
    JSON.stringify(fullEnvelope),
  );
}

export function clearViewStateEnvelope(
  storage: StorageLike,
  userId: string | null | undefined,
  routeKey: string,
  contextKey = 'default',
  version = DEFAULT_VIEW_STATE_VERSION,
): void {
  if (!userId) return;
  storage.removeItem(viewStateStorageKey(userId, routeKey, contextKey, version));
}

export function clearUserViewState(storage: StorageLike, userId: string | null | undefined): void {
  if (!userId || typeof window === 'undefined') return;
  const userPart = `:${safeKeyPart(userId)}:`;
  Object.keys(window.sessionStorage)
    .filter((key) => key.startsWith(`${keyPrefix}:`) && key.includes(userPart))
    .forEach((key) => storage.removeItem(key));
}
